// Lib
import { FC, useEffect, useState } from "react";
// Api
import {
  useLazyGetNotificationsSettingsQuery,
  useUpdateNotificationsSettingsMutation,
} from "rtkQuery/query/notificationsSettingsAPI";
// Hooks
import { useNotification, usePermissions } from "hooks";
// Helpers
import { getConfirmModalTitle } from "../../helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { PersonIcon } from "icons";
// Components
import { ConfirmDialog } from "components/Modals";
import { SettingsItem } from "../SettingsItem";
import { Switch } from "../Inputs";
// Styled
import { ContentBox } from "styled/Box";
import { Typography } from "styled/Typography";

import {
  ConfirmModalType,
  initialLoadingState,
  initialState,
  LoadingStateValues,
  NotificationField,
  notificationMessage,
  StateValues,
} from "./config";
import { getSettingsName } from "pages/Settings/helpers";

export const NotificationsSettings: FC = () => {
  const { openNotification } = useNotification();

  const { isSuperAdminManagement } = usePermissions();

  const [fetchSettings] = useLazyGetNotificationsSettingsQuery();
  const [updateSettings] = useUpdateNotificationsSettingsMutation();

  const [settings, setSettings] = useState<StateValues>(initialState);

  const [isLoading, setIsLoading] =
    useState<LoadingStateValues>(initialLoadingState);

  const [confirmModal, setConfirmModal] = useState<ConfirmModalType | false>(
    false,
  );

  const getSettings = async () => {
    try {
      setIsLoading(prev => ({
        ...prev,
        ...Object.fromEntries(Object.values(prev).map(field => [field, true])),
      }));

      const { isNotificationsEnabled } = await fetchSettings().unwrap();

      setSettings(prev => ({
        ...prev,
        [NotificationField.IsNotificationsEnabled]: isNotificationsEnabled,
      }));
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        ...Object.fromEntries(Object.values(prev).map(field => [field, false])),
      }));
    }
  };

  const handleUpdateAuthSettings = async (
    key: NotificationField,
    value: boolean,
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [key]: true }));

      const payload = {
        [key]: value,
      };

      setSettings(prev => ({ ...prev, [key]: value }));

      await updateSettings({ notifications: payload }).unwrap();

      openNotification({ message: notificationMessage[key] });
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));

      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(prev => ({ ...prev, [key]: false }));

      if (confirmModal) {
        setConfirmModal(false);
      }
    }
  };

  const handleUpdate = (
    key: NotificationField,
    value: StateValues[NotificationField],
  ) => {
    switch (key) {
      case NotificationField.IsNotificationsEnabled:
        setSettings(prev => ({ ...prev, [key]: value as boolean }));
        setConfirmModal({ key, value });
        return;

      default:
        break;
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  const onConfirm = () => {
    if (!confirmModal) return;

    switch (confirmModal.key) {
      case NotificationField.IsNotificationsEnabled:
        handleUpdateAuthSettings(
          confirmModal.key,
          confirmModal.value as boolean,
        );
        return;

      default:
        break;
    }
  };

  const onCancel = () => {
    if (!confirmModal) return;
    setSettings(prev => ({ ...prev, [confirmModal.key]: !confirmModal.value }));
    setConfirmModal(false);
  };

  return (
    <>
      {isSuperAdminManagement && (
        <ContentBox $column $gap={12}>
          <Typography.H2>Notifications</Typography.H2>

          <div>
            <SettingsItem
              title={getSettingsName(NotificationField.IsNotificationsEnabled)}
            >
              <Switch
                isLoading={isLoading[NotificationField.IsNotificationsEnabled]}
                canUpdate={isSuperAdminManagement}
                data={settings[NotificationField.IsNotificationsEnabled]}
                onChange={value =>
                  handleUpdate(NotificationField.IsNotificationsEnabled, value)
                }
              />
            </SettingsItem>
          </div>
        </ContentBox>
      )}

      <ConfirmDialog
        isLoading={confirmModal && isLoading[confirmModal.key]}
        open={!!confirmModal}
        Icon={PersonIcon}
        message={
          confirmModal &&
          getConfirmModalTitle({
            key: confirmModal.key,
            value: confirmModal.value,
          })
        }
        description="Are you sure to continue this action?"
        onCancel={onCancel}
        firstCTAButton={{
          title: "Accept",
          status: "danger",
          disabled: false,
          loading: confirmModal && isLoading[confirmModal.key],
          onClick: onConfirm,
        }}
      />
    </>
  );
};
